import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { Card, Flex, Typography } from 'antd';

import isEmpty from 'lodash.isempty';

import { MAPS_TAB } from '../../../constants/map';
import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { createFarm } from '../../../redux/actions/farm';

import CancelSaveButtons from '../../../components/CancelSaveButtons';
import CitySelector from '../../../components/CitySelector';
import Map from '../../../components/Maps/MapFarm';
import { DEFAULT_SHAPE_COLOR, TYPE } from '../../../components/Maps/constants';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

import FarmCreateForm from './FarmCreateForm';
import './styles.css';

const { Title, Text } = Typography;

function NewFarm() {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);

  const { user } = useSelector(state => state.user);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [address, setAddress] = useState(null);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    color: DEFAULT_SHAPE_COLOR,
    userAddress: '',
    editing: false,
    farm: {
      address: {},
      circumference: '',
      description: '',
      farm_rules: [],
      full_address: '',
      geofence_id: '',
      geofences: [],
      holdin_number: '',
      id: null,
      master_geofence: null,
      name: '',
      size: ''
    },
    geofences: [],
    selected_country: {},
    selected_geofences: []
  });

  useEffect(() => {
    prepareInitData();
  }, []);

  function prepareInitData() {
    if (user?.address?.country || user?.address?.city) {
      setState(prevState => ({
        ...prevState,
        userAddress: user?.address?.city
          ? `${user?.address?.country},${user?.address?.city}`
          : `${user?.address?.country}`
      }));
    }

    let farm = {
      name: '',
      address: '',
      full_address: '',
      description: '',
      holding_numbers: 0,
      geofences: []
    };

    setState(prevState => ({
      ...prevState,
      farm: farm,
      selected_geofences: farm.geofences.map(gf => ({
        value: gf.id,
        label: gf.name,
        geofence: gf
      })),
      has_fence: false
    }));
  }

  const changeColor = useCallback(
    color => {
      setState(prevState => ({ ...prevState, color }));
    },
    [state.color]
  );

  function setMasterGeofenceGeoJson(geo_json) {
    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      farm: {
        ...prevState.farm,
        master_geofence: { geo_json }
      },
      has_fence: true
    }));
  }

  async function handleCreateFarm() {
    const success = await createNewFarm();

    if (success) {
      setIsFormDirty(false);
      history.push(`/maps?tab=${MAPS_TAB.FARM}`);
    }
  }

  async function createNewFarm() {
    if (!state.has_fence || isEmpty(state.farm.master_geofence?.geo_json)) {
      warnToastHandler(messages.MASTER_GEOFENCE_REQUIRED);
      return;
    }

    const values = await formRef.current?.validateFields();
    const fullAddress = {
      city: address?.city,
      country: address?.country,
      house_number: values.house_number,
      postal_code: values.postal_code,
      street: values.street
    };
    const holdingNumbers = state.farm.holding_numbers ? [state.farm.holding_numbers.toString()] : [];

    try {
      await dispatch(
        createFarm({
          address: fullAddress,
          color: state.color,
          description: values?.description,
          holding_numbers: holdingNumbers,
          master_geofence: state.farm.master_geofence?.geo_json,
          name: values?.name
        })
      ).unwrap();

      successToastHandler(messages.CREATED);

      return true;
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_CREATE_DATA('farm'));

      return false;
    }
  }

  const changeAddress = useCallback((_, option) => {
    const { country, city } = option ?? user?.address;

    setIsFormDirty(!!option);
    setState(prev => ({
      ...prev,
      userAddress: `${city}, ${country}` ?? `${user?.address?.city}, ${user?.address?.country}`
    }));
    setAddress({ country, city });
  }, []);

  const handleDeleteShape = () => {
    setSubmittable(false);
    warnToastHandler(messages.MASTER_GEOFENCE_REQUIRED);
  };

  const breadcrumbItems = [
    {
      title: <Link to={`/maps?tab=${MAPS_TAB.FARM}`}>List of Farms</Link>
    },
    {
      title: 'Create New Farm'
    }
  ];

  const validate = () => {
    const farmName = formRef?.current?.getFieldValue('name');

    if (typeof farmName === 'string' && farmName.trim() !== '') {
      setSubmittable(true);
    }
  };

  const handleCancelEditing = useCallback(() => {
    history.push(`/maps?tab=${MAPS_TAB.FARM}`);
  }, []);

  const { NavigationPrompt, modalProps, nextLocation, confirmed } = useUnsavedChangesPrompt({
    when: isFormDirty,
    onSaveAndLeave: async () => {
      if (submittable) {
        await handleCreateFarm();

        return true;
      } else {
        warnToastHandler(messages.DATA_REQUIRED);
        formRef.current?.validateFields();

        return false;
      }
    },
    onDiscardAndLeave: () => {
      formRef.current?.resetFields();
      setIsFormDirty(false);
    }
  });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  return (
    <>
      {NavigationPrompt}
      <Flex gap="small" vertical={false} justify="space-between" wrap>
        <Flex vertical={true}>
          <Title level={4}>New Farm {state.farm.id}</Title>

          <AppBreadcrumb items={breadcrumbItems} />
        </Flex>
        <Flex>
          <CancelSaveButtons
            disabled={!submittable || !state.farm.master_geofence?.geo_json}
            handleCancel={handleCancelEditing}
            handleSave={handleCreateFarm}
          />
        </Flex>
      </Flex>

      <Card className="mb-2">
        <Text type="secondary">Mark the size and borders of the farm.</Text>
        <Flex align="center" className="mb-2 flex-wrap flex-sm-nowrap" gap={'0 10px'}>
          <Text strong className="flex-fill text-nowrap">
            Closest town or city
          </Text>
          <CitySelector
            className="flex-fill w-100"
            defaultValue={`${user?.address?.city}, ${user?.address?.country}`}
            handleClear={changeAddress}
            handleSelect={changeAddress}
          />
        </Flex>

        {state.userAddress && (
          <>
            <Map
              color={state.color}
              defaultPosition={state.userAddress}
              editing={true}
              farm={state.farm}
              isNew={true}
              type={TYPE.FARM}
              editFence={setMasterGeofenceGeoJson}
              handleCancelEditing={handleCancelEditing}
              handleSaveEditing={handleCreateFarm}
              handleDelete={handleDeleteShape}
              onCreateGeofence={setMasterGeofenceGeoJson}
              setSubmittable={validate}
            />

            <FarmCreateForm
              address={user?.address}
              color={state.color}
              formRef={formRef}
              handleColorChange={changeColor}
              onChange={setIsFormDirty}
              setSubmittable={setSubmittable}
            />
          </>
        )}
      </Card>

      <Flex justify="end" className="mb-2">
        <CancelSaveButtons
          disabled={!submittable || !state.farm.master_geofence?.geo_json}
          handleCancel={handleCancelEditing}
          handleSave={handleCreateFarm}
        />
      </Flex>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default memo(NewFarm);
